import React from 'react'
import { useI18n } from '../../i18n'
import { Network, FileCheck, ShieldAlert, MoreHorizontal, FileText } from 'lucide-react'
import LifecycleBadge from './LifecycleBadge'
import ReadinessHero from './ReadinessHero'
import SeverityDot, { severityBadgeToneClass } from './SeverityDot'
import { useAuditReadiness } from './useAuditReadiness'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import type {
  OperationalSummaryRow,
  TopVulnerability,
  TopFailedControl,
  TopOpenRisk,
} from '../../services/informationSystemOperationalApi'
import type { DrillDownTarget } from './operationalDrillDownContent'

function criticalityClass(criticality: string | null | undefined): string {
  const c = String(criticality || '').toLowerCase()
  if (c === 'critical') return styles.metaChipCritical
  if (c === 'high') return styles.metaChipHigh
  if (c === 'medium') return styles.metaChipMedium
  if (c === 'low') return styles.metaChipLow
  return styles.metaChipNeutral
}

export function SectionCell({
  label,
  children,
  first,
  onActivate,
  className = '',
}: {
  label: string
  children: React.ReactNode
  first?: boolean
  onActivate?: () => void
  className?: string
}) {
  return (
    <section
      className={`${styles.rowSection} ${first ? styles.rowSectionFirst : ''} ${onActivate ? styles.rowSectionClickable : ''} ${!first ? styles.sectionDivider : ''} ${className}`.trim()}
      onClick={
        onActivate
          ? (e) => {
              e.stopPropagation()
              onActivate()
            }
          : undefined
      }
      onKeyDown={
        onActivate
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onActivate()
              }
            }
          : undefined
      }
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
    >
      <p className={styles.sectionHeader} title={label}>
        {label}
      </p>
      {children}
    </section>
  )
}

export function SystemIdentityCell({ row }: { row: OperationalSummaryRow }) {
  const { t } = useI18n()

  return (
    <section className={`${styles.systemIdentity} ${styles.rowSectionFirst}`}>
      <header className={styles.identityHeader}>
        <h4 className={styles.systemName} title={row.name}>
          {row.name}
        </h4>
        <LifecycleBadge status={row.lifecycleStatus} />
      </header>
      {row.description ? <p className={styles.systemDescription}>{row.description}</p> : null}
      <p className={styles.systemMetaRow}>
        {row.environment ? <span className={styles.metaChipNeutral}>{row.environment}</span> : null}
        {row.criticality ? <span className={criticalityClass(row.criticality)}>{row.criticality}</span> : null}
        {row.ownerLabel ? (
          <span className={styles.metaChipNeutral} title={row.ownerLabel}>
            {t('assets.postureRow.owner', 'Owner')}: {row.ownerLabel}
          </span>
        ) : null}
      </p>
    </section>
  )
}

export function ChipGroup({
  items,
  emptyLabel,
  variant = 'violet',
  onChipClick,
}: {
  items: string[]
  emptyLabel: string
  variant?: 'violet' | 'amber'
  onChipClick?: () => void
}) {
  const shown = items.slice(0, 3)
  const extra = items.length - shown.length
  const chipVariant =
    variant === 'amber' ? `${styles.chip} ${styles.standardsChip}` : `${styles.chip} ${styles.dataChip}`

  if (!items.length) {
    return (
      <button
        type="button"
        className={styles.emptyHintBtn}
        onClick={(e) => {
          e.stopPropagation()
          onChipClick?.()
        }}
      >
        {emptyLabel}
      </button>
    )
  }

  return (
    <span
      className={styles.chipGroup}
      onClick={(e) => {
        e.stopPropagation()
        onChipClick?.()
      }}
      role={onChipClick ? 'button' : undefined}
      tabIndex={onChipClick ? 0 : undefined}
    >
      {shown.map((item) => (
        <span key={item} className={chipVariant} title={item}>
          {item}
        </span>
      ))}
      {extra > 0 ? <span className={`${styles.chip} ${styles.chipMore}`}>+{extra}</span> : null}
    </span>
  )
}

export function TopVulnerabilitiesList({
  items,
  empty,
  onDrillDown,
  systemId,
}: {
  items: TopVulnerability[]
  empty: string
  onDrillDown: (t: DrillDownTarget) => void
  systemId: string
}) {
  if (!items.length) return <p className={styles.emptyHint}>{empty}</p>
  return (
    <ul className={styles.itemList}>
      {items.map((v) => {
        const assets = (v.affectedAssetNames || []).slice(0, 3).join(' / ')
        const label = v.cve || v.title
        return (
          <li key={v.id || v.fingerprint || label}>
            <button
              type="button"
              className={styles.itemButton}
              onClick={(e) => {
                e.stopPropagation()
                onDrillDown({ kind: 'vulnerability', id: v.id, label, systemId })
              }}
            >
              <span className={styles.itemRow}>
                <SeverityDot severity={v.severity} />
                <span className={styles.itemContent}>
                  <span className={styles.itemTitle}>{label}</span>
                  {assets ? <span className={styles.itemSub}>{assets}</span> : null}
                  <span className={`${styles.severityBadge} ${severityBadgeToneClass(v.severity)}`}>{v.severity}</span>
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

export function TopFailedControlsList({
  items,
  empty,
  onDrillDown,
  systemId,
}: {
  items: TopFailedControl[]
  empty: string
  onDrillDown: (t: DrillDownTarget) => void
  systemId: string
}) {
  if (!items.length) return <p className={styles.emptyHint}>{empty}</p>
  return (
    <ul className={styles.itemList}>
      {items.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            className={`${styles.itemButton} ${styles.itemButtonControl}`}
            onClick={(e) => {
              e.stopPropagation()
              onDrillDown({ kind: 'control', id: c.id, label: c.controlTitle || c.controlId, systemId })
            }}
          >
            <span className={styles.itemRow}>
              <SeverityDot severity="HIGH" />
              <span className={styles.itemContent}>
                <span className={styles.itemTitle}>{c.controlTitle || c.controlId}</span>
                {c.standardCode ? (
                  <span className={styles.itemSub}>{c.standardCode}</span>
                ) : c.implementationStatus ? (
                  <span className={styles.itemSub}>{c.implementationStatus.replace(/_/g, ' ')}</span>
                ) : null}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export function TopOpenRisksList({
  items,
  empty,
  onDrillDown,
  systemId,
}: {
  items: TopOpenRisk[]
  empty: string
  onDrillDown: (t: DrillDownTarget) => void
  systemId: string
}) {
  if (!items.length) return <p className={styles.emptyHint}>{empty}</p>
  return (
    <ul className={styles.itemList}>
      {items.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            className={styles.itemButton}
            onClick={(e) => {
              e.stopPropagation()
              onDrillDown({
                kind: 'risk',
                sourceType: r.sourceType,
                sourceId: r.sourceId || r.id,
                label: r.title,
                systemId,
              })
            }}
          >
            <span className={styles.itemRow}>
              <SeverityDot severity={r.severity} />
              <span className={styles.itemContent}>
                <span className={styles.itemTitle}>{r.title}</span>
                {r.severity ? (
                  <span className={`${styles.severityBadge} ${severityBadgeToneClass(r.severity)}`}>{r.severity}</span>
                ) : null}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export function PostureMiniWidget({
  row,
  onOpenReadiness,
}: {
  row: OperationalSummaryRow
  onOpenReadiness: () => void
  onOpenBlockers?: () => void
}) {
  const audit = useAuditReadiness(row)

  return (
    <ReadinessHero
      variant="row"
      model={audit}
      onClick={onOpenReadiness}
      className={styles.postureWidget}
    />
  )
}

export function RowQuickActions({
  onOpenPassport,
  onOpenGraph,
  onOpenEvidence,
  onOpenRemediation,
  onOpenControls,
  onMore,
  menuOpen,
  onToggleMenu,
  onEdit,
  onArchive,
  onDelete,
}: {
  onOpenPassport: () => void
  onOpenGraph: () => void
  onOpenEvidence: () => void
  onOpenRemediation: () => void
  onOpenControls?: () => void
  onMore: () => void
  menuOpen: boolean
  onToggleMenu: () => void
  onEdit?: () => void
  onArchive?: () => void
  onDelete?: () => void
}) {
  const { t } = useI18n()
  const menuRef = React.useRef<HTMLSpanElement>(null)

  React.useEffect(() => {
    if (!menuOpen) return
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onToggleMenu()
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen, onToggleMenu])

  return (
    <nav className={`${styles.rowActions} ${styles.sectionDivider}`} onClick={(e) => e.stopPropagation()} aria-label={t('assets.postureRow.actions', 'Actions')}>
      <button type="button" className={styles.rowIconBtn} onClick={onOpenGraph} title={t('assets.postureRow.openGraph', 'Graph')}>
        <Network size={16} aria-hidden />
      </button>
      <button type="button" className={styles.rowIconBtn} onClick={onOpenEvidence} title={t('assets.postureRow.evidence', 'Evidence')}>
        <FileCheck size={16} aria-hidden />
      </button>
      <button type="button" className={styles.rowIconBtn} onClick={onOpenRemediation} title={t('assets.postureRow.remediation', 'Remediation')}>
        <ShieldAlert size={16} aria-hidden />
      </button>
      <button type="button" className={styles.rowIconBtnSubtle} onClick={onOpenPassport} title={t('assets.postureRow.openPassport', 'Passport')}>
        <FileText size={14} aria-hidden />
      </button>
      <span className={styles.menuWrap} ref={menuRef}>
        <button
          type="button"
          className={styles.rowIconBtn}
          onClick={onToggleMenu}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={t('assets.postureRow.moreActions', 'More actions')}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
        {menuOpen ? (
          <span className={styles.menuDropdown} role="menu">
            {onEdit ? (
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => { onEdit(); onToggleMenu() }}>
                {t('common.edit', 'Edit')}
              </button>
            ) : null}
            {onArchive ? (
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => { onArchive(); onToggleMenu() }}>
                {t('assets.lifecycle.archive', 'Archive')}
              </button>
            ) : null}
            {onDelete ? (
              <button type="button" className={`${styles.menuItem} ${styles.menuItemDanger}`} role="menuitem" onClick={() => { onDelete(); onToggleMenu() }}>
                {t('common.delete', 'Delete')}
              </button>
            ) : null}
            {onOpenControls ? (
              <button type="button" className={styles.menuItem} role="menuitem" onClick={() => { onOpenControls(); onToggleMenu() }}>
                {t('assets.postureRow.openControls', 'Controls')}
              </button>
            ) : null}
            <button type="button" className={styles.menuItem} role="menuitem" onClick={() => { onMore(); onToggleMenu() }}>
              {t('assets.postureRow.selectRow', 'Select')}
            </button>
          </span>
        ) : null}
      </span>
    </nav>
  )
}
